import { ITranslationMap } from '@principle-theorem/practice-migrations/interfaces';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';
import { IStaffer } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  WithPrimaryKey,
} from '@principle-theorem/shared';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { IBaseMigrationStaffer } from '../../../interfaces';
import {
  STAFFER_MAPPING,
  StafferMappingHandler,
} from '../../../mappings/staff';
import { TranslationMapHandler } from '../../../translation-map';
import {
  D4WStaffer,
  StafferSourceEntity,
  type ID4WStaffer,
} from '../../source/entities/staff';

export class D4WStafferMappingHandler extends StafferMappingHandler<
  D4WStaffer,
  StafferSourceEntity
> {
  override sourceEntity = new StafferSourceEntity();
  override customMapping = {
    ...STAFFER_MAPPING,
    metadata: {
      ...STAFFER_MAPPING.metadata,
      allowManualInput: true,
    },
  };

  translateFn = (record: ID4WStaffer): IBaseMigrationStaffer => ({
    id: record.id,
    name: `${record.first_name} ${record.surname}`,
    providerNo: record.provider_no,
  });
}

export async function resolveD4WStaffer(
  staffId: string,
  staffMappings: WithPrimaryKey<ITranslationMap<IStaffer>>[],
  translationMap: TranslationMapHandler,
  allowDefault = true
): Promise<INamedDocument<IStaffer>> {
  const foundMapping = staffMappings.find(
    (staffMapping) => staffMapping.sourceIdentifier === staffId
  )?.destinationIdentifier;
  if (foundMapping) {
    const staffer = await Firestore.getDoc(foundMapping);
    return stafferToNamedDoc(staffer);
  }

  const resolvedMapping = await translationMap.getBySource(
    staffId,
    STAFFER_RESOURCE_TYPE
  );
  if (resolvedMapping?.destinationIdentifier) {
    const staffer = await Firestore.getDoc(
      resolvedMapping.destinationIdentifier as DocumentReference<IStaffer>
    );
    return stafferToNamedDoc(staffer);
  }

  if (!allowDefault) {
    throw new Error(`Couldn't find staff with id ${staffId}`);
  }

  const defaultMap = staffMappings.find(
    (staffMapping) => staffMapping.sourceIdentifier === '0'
  )?.destinationIdentifier;

  if (!defaultMap) {
    throw new Error('Default staff mapping not found');
  }
  const defaultPractitioner = await Firestore.getDoc(defaultMap);
  return stafferToNamedDoc(defaultPractitioner);
}
